// File handling for the Better TDL (.tdl) format. Parsing/validation and
// legacy migration live in serialization.ts; this module handles file I/O.
import { TodoTree, TodayList } from '../types/todo';
import { loadTdlContent } from './serialization';
import { flattenTree } from './tree/traversal';
import { FILE_PICKER, FILES } from './constants';

/**
 * Converts a filename to use .tdl extension
 * If the filename ends with .json, replaces it with .tdl
 * Otherwise, appends .tdl if no extension is present
 */
export const toTdlFilename = (filename: string): string => {
  if (filename.toLowerCase().endsWith(FILES.JSON_EXTENSION)) {
    return filename.slice(0, -FILES.JSON_EXTENSION.length) + FILES.TDL_EXTENSION;
  }
  if (filename.toLowerCase().endsWith(FILES.TDL_EXTENSION)) {
    return filename;
  }
  return filename + FILES.TDL_EXTENSION;
};

export interface FileData {
  filename: string;
  content: string;
  isValid: boolean;
  error?: string;
}

export const readJsonFile = (file: File): Promise<FileData> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const result: FileData = {
        filename: file.name,
        content,
        isValid: true,
      };

      try {
        JSON.parse(content);
      } catch (error) {
        result.isValid = false;
        result.error = error instanceof Error ? error.message : 'Invalid JSON';
      }

      resolve(result);
    };

    reader.onerror = () => {
      resolve({
        filename: file.name,
        content: '',
        isValid: false,
        error: 'Failed to read file',
      });
    };

    reader.readAsText(file);
  });
};

export const validateJson = (jsonString: string): { isValid: boolean; error?: string } => {
  try {
    JSON.parse(jsonString);
    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Error ? error.message : 'Invalid JSON',
    };
  }
};

export const downloadFile = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Enhanced download function that uses the native File System Access API when available,
 * falling back to the traditional download method for older browsers.
 * Files are saved with .tdl extension for the new format.
 */
export const downloadFileWithDialog = async (content: string, filename: string): Promise<boolean> => {
  // Convert filename to .tdl format
  const tdlFilename = toTdlFilename(filename);

  // Check if File System Access API is supported
  if (window.showSaveFilePicker) {
    try {
      // Use modern File System Access API
      const handle = await window.showSaveFilePicker({
        suggestedName: tdlFilename,
        types: [{
          description: FILE_PICKER.TDL_TYPE.description,
          accept: { 'application/json': [...FILE_PICKER.TDL_TYPE.accept['application/json']] }
        }],
        // Start in Downloads folder if possible
        startIn: FILE_PICKER.START_IN
      });

      // Create a writable stream and write our file
      const writable = await handle.createWritable();
      await writable.write(content);
      await writable.close();

      return true; // Success
    } catch (err: any) {
      // User cancelled the dialog
      if (err.name === 'AbortError') {
        return false; // User cancelled, not an error
      }

      // Other errors, fall back to traditional method
      console.warn('File System Access API failed, falling back to traditional download:', err);
      downloadFile(content, tdlFilename);
      return true;
    }
  } else {
    // Fall back to traditional download for unsupported browsers
    downloadFile(content, tdlFilename);
    return true;
  }
};

export interface EnhancedFileData extends FileData {
  parsedData?: any;
  todoTree?: TodoTree;
  today?: TodayList;
}

export const readTodoFile = async (file: File): Promise<EnhancedFileData> => {
  const basicFileData = await readJsonFile(file);

  if (!basicFileData.isValid) {
    return basicFileData;
  }

  // Accepts the current v2 format and migrates the legacy nested format.
  const result = loadTdlContent(basicFileData.content);
  if (result.error || !result.tree) {
    return {
      ...basicFileData,
      isValid: false,
      error: result.error ?? 'Failed to parse todo file',
    };
  }

  return {
    ...basicFileData,
    todoTree: result.tree,
    today: result.today,
  };
};

/**
 * Converts a TodoTree to a Markdown string representation
 * Uses flattenTree for depth-first ordering with proper indentation
 */
export function treeToMarkdown(tree: TodoTree): string {
  const items = flattenTree(tree);
  const lines: string[] = [];
  const archivedItems: Array<{ id: string; item: typeof items[0]['item']; depth: number }> = [];

  for (const { item, depth } of items) {
    if (item.archived) {
      archivedItems.push({ id: '', item, depth });
      continue;
    }

    if (depth === 0) {
      // Root node as heading
      lines.push(`# ${item.name}`);
      lines.push('');
      continue;
    }

    const indent = '  '.repeat(depth - 1);
    const checkbox = item.done ? '[x]' : '[ ]';
    let line = `${indent}- ${checkbox} ${item.name}`;

    // Add priority if not medium
    if (item.priority !== 'medium') {
      line += ` [${item.priority}]`;
    }

    // Add blocked indicator
    if (item.blocked) {
      line += ' **BLOCKED**';
    }

    lines.push(line);

    // Add description indented below
    if (item.description) {
      const descIndent = '  '.repeat(depth);
      const descLines = item.description.split('\n');
      for (const descLine of descLines) {
        lines.push(`${descIndent}${descLine}`);
      }
    }
  }

  // Archived section
  if (archivedItems.length > 0) {
    lines.push('');
    lines.push('## Archived');
    lines.push('');
    for (const { item, depth } of archivedItems) {
      const indent = '  '.repeat(Math.max(0, depth - 1));
      const checkbox = item.done ? '[x]' : '[ ]';
      lines.push(`${indent}- ${checkbox} ~~${item.name}~~`);
    }
  }

  return lines.join('\n') + '\n';
}

/**
 * Downloads content as a Markdown file using File System Access API or fallback
 */
export const downloadMarkdownWithDialog = async (content: string, filename: string): Promise<boolean> => {
  // Ensure .md extension
  const mdFilename = filename.replace(/\.(tdl|json)$/i, '') + '.md';

  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: mdFilename,
        types: [{
          description: 'Markdown Files',
          accept: { 'text/markdown': ['.md'] },
        }],
        startIn: FILE_PICKER.START_IN,
      });

      const writable = await handle.createWritable();
      await writable.write(content);
      await writable.close();
      return true;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return false;
      }
      // Fall back to traditional download
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = mdFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    }
  } else {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = mdFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  }
};